'use client'

import { useState } from 'react'
import type { TestimonialSectionFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import { StructuredText, renderNodeRule } from 'react-datocms'
import { TextWithHighlight } from '../TextWithHighlight'
import Eyebrow from '../ui/Eyebrow'
import { isParagraph } from 'datocms-structured-text-utils'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'
import clsx from 'clsx'
import TestimonialCard from '../TestimonialCard'
import Carousel from '../Carousel'

export default function TestimonialSection({
	tag,
	title,
	testimonials,
}: Readonly<TestimonialSectionFragment>) {
	const [selectedCard, setSelectedCard] = useState<number>(0)

	if (!testimonials.length) return null
	return (
		<section className="testimonial-section @container">
			<div className="max-w-screen-2xl mx-auto pt-16 pb-6 md:py-24">
				<div className="common-x-padding flex max-md:flex-col gap-6 justify-between mb-14 @5xl:mb-24">
					{tag && <Eyebrow>{tag}</Eyebrow>}
					{title && !isStructuredTextEmpty(title.text) && (
						<div className="heading-size-2 !font-semibold md:w-2/3 @5xl:w-1/2">
							<TextWithHighlight text={title} />
						</div>
					)}
				</div>

				{/* If there are  1-2 testimonials, display them on a grid */}
				{testimonials.length <= 2 && (
					<div className="grid grid-cols-1 @lg:grid-cols-2 gap-4 common-x-padding mt-10 @5xl:mt-20">
						{testimonials.map((testimonial) => (
							<TestimonialCard key={testimonial.id as string} {...testimonial} />
						))}
					</div>
				)}

				{/* If there are 3 testimonials, display them as an accordion */}
				{testimonials.length === 3 && (
					<div className="flex gap-2.5 md:gap-5 text-white overflow-x-auto scroll-bar rounded-xl px-3 md:px-10 pb-6">
						{testimonials.map((testimonial, index) => (
							<button
								key={testimonial.id as string}
								className={`group relative text-left min-w-[342px] @5xl:min-w-none rounded-xl overflow-hidden h-[540px] @5xl:h-[670px] max-md:border-b-8 border-blue after:content-[''] after:absolute after:inset-0 after:bg-gradient-to-t transition-all duration-500 ease-in-out max-md:select-none max-md:cursor-default ${selectedCard === index ? 'w-1/2 after:from-black/60 after:to-black/60' : '@5xl:w-1/4 after:from-black/40 after:to-transparent'}`}
								onClick={() => setSelectedCard(index)}
							>
								{testimonial.image?.[0]?.url && (
									<Image
										src={testimonial.image?.[0]?.url as string}
										alt={(testimonial.image?.[0]?.alt as string) ?? 'Testimonial Image'}
										width={testimonial.image?.[0]?.width as number}
										height={testimonial.image?.[0]?.height as number}
										className={clsx(
											'absolute w-full h-full inset-0 object-cover  transition-transform duration-500 m-ease',
											selectedCard === index ? 'scale-[103%]' : 'group-hover:scale-[103%]'
										)}
										sizes="(max-width: 1024px) 342px, 30vw"
									/>
								)}

								<div className="absolute flex flex-col justify-end inset-y-0 pt-8 md:pt-10 inset-x-6 md:inset-x-10 z-10">
									<div className="relative sm:pl-3 max-sm:before:hidden before:content-[''] before:absolute before:w-1 before:-left-0 before:inset-y-1 before:bg-blue">
										{testimonial.personName && (
											<p className="text-2xl font-semibold">{testimonial.personName}</p>
										)}
										{testimonial.jobTitle && <p>{testimonial.jobTitle}</p>}
									</div>
									{testimonial.quote && (
										<blockquote
											className={`grid text-lg  lg:text-2xl leading-tight overflow-hidden transition-all duration-300 ease-s-ease max-md:pt-6 ${selectedCard === index ? 'grid-rows-[1fr] opacity-100 md:pt-6 delay-[400ms]' : 'grid-rows-[0fr] opacity-0'}`}
										>
											<StructuredText
												data={testimonial.quote}
												customNodeRules={[
													renderNodeRule(isParagraph, ({ children, key }) => {
														return (
															<div
																className="scroll-hidden overflow-scroll pb-8 lg:pb-10"
																key={key}
															>
																&ldquo;{children}&rdquo;
															</div>
														)
													}),
												]}
											/>
										</blockquote>
									)}
								</div>
							</button>
						))}
					</div>
				)}

				{/* If there are more than 3 testimonials, display them in a Carousel */}
				{testimonials.length > 3 && (
					<Carousel
						sliderClassName="@5xl:rounded-xl mt-10 @5xl:mt-20 px-3 @3xl:px-10"
						clickAndScroll
					>
						{testimonials.map((testimonial) => (
							<TestimonialCard
								key={testimonial.id as string}
								{...testimonial}
								className="min-w-[90%] md:min-w-[570px]"
							/>
						))}
					</Carousel>
				)}
			</div>
		</section>
	)
}
